import { Container } from 'ui';
import { FunctionComponent } from 'react';
import { Head } from 'head/Head';
import { Link, Typography } from 'omakase';
import {
  cookiePolicyLink,
  helpCenterLink,
  privacyEmailAnchor,
  privacySpecificLink,
  termsLink,
} from 'utils/legal/termsUtils';

const PrivacyRoute: React.FunctionComponent = () => {
  const H: FunctionComponent<{}> = ({ children }) => {
    return (
      <h4>
        <Typography color="inherit" component="h2" variant="heading02">
          {children}
        </Typography>
      </h4>
    );
  };

  const P: FunctionComponent<{}> = ({ children }) => {
    return (
      <p>
        <Typography color="inherit" component="span" variant="body02">
          {children}
        </Typography>
      </p>
    );
  };
  const effectiveDate = 'November 24, 2025';
  const frenchEffectivesDate = '24 novembre 2025';

  return (
    <>
      <Head
        title="Tock - Privacy Policy"
        description="Tock privacy policy"
        keywords="privacy policy, terms, conditions"
      />
      <div className="Legal">
        <Container maxWidth="md">
          <Typography component="h1" variant="heading01">
            Privacy Policy
          </Typography>
          <Typography variant="body01Strong" mb={2}>
            Effective Date: {effectiveDate}
          </Typography>
          <P>
            Tock LLC (“Tock” “we”, “us” or “our”) is commited to safeguarding your privacy. This
            Privacy Policy describes the personal information we collect, how we use and disclose
            that information, and your choices and rights. Tock is owned by{' '}
            <Link href="https://www.americanexpress.com/">American Express</Link>.
          </P>
          <P>
            By using any of Tock's Services, you confirm you have read and understood this Privacy
            Policy and our {cookiePolicyLink()}.
          </P>
          <H>1. Some key terms</H>
          <P>
            Any capitalized terms not defined in this Privacy Policy have the meanings in our{' '}
            {termsLink()}.
          </P>
          <H>2. What information does this Privacy Policy apply to? </H>
          <P>
            This Privacy Policy applies to the personal information that we collect when you use our
            products or services, website, mobile applications, or as otherwise described in this
            Privacy Policy. For example, this Privacy Policy applies to Guest and Merchant account
            information and information about how Guests and Merchants use and interact with our
            Services, including information Guests and Merchants submit for customer support. This
            Privacy Policy does not apply to personal information of our employees or job applicants
            (except to the extent employees or job applicants are Guests or Merchants).
          </P>
          <P>
            With respect to personal information in Tock's possession, we play a few different roles
            under global data privacy laws. In order to understand your and Tock's obligations, it's
            important to understand the difference between Tock Controlled PI and Merchant
            Controlled PI.
          </P>
          <Typography component="ul">
            <Typography component="li">
              “Tock Controlled PI” means personal information for which Tock determines the purposes
              and means of processing. This Privacy Policy and{' '}
              {privacySpecificLink('Specific Privacy Notices')} only address Tock Controlled PI.
            </Typography>
            <Typography component="li">
              “Merchant Controlled PI” means personal information for which a Merchant determines
              the purposes and means of processing. For Merchant Controlled PI, Tock acts as a data
              processor, service provider or similar term under applicable law. Merchant Controlled
              PI includes Guest Booking Information (as defined below) and any data or notes entered
              by a Merchant into the Services about a Guest or their partner or other dining
              companions, Merchant personnel or other individuals. We use Merchant Controlled PI at
              the direction of our Merchants, and our Merchants are responsible for ensuring that
              their collection and processing of Merchant Controlled PI complies with applicable law
              and their respective privacy policy.
            </Typography>
          </Typography>
          <P>
            To learn about a Merchant's data practices with respect to Merchant Controlled PI,
            please visit the applicable Merchant's privacy policy.
          </P>

          <H>3. Personal information we collect</H>
          <P>We collect the following types of personal information about you:</P>
          <Typography component="ul">
            <Typography component="li">
              Information a Guest provides to create an Account or make a Booking, such as email
              address, first name, last name and phone number. This information is “Basic Guest
              Information.” You do not need an Account to browse our Services (including Merchant
              Pages), but you do need an Account to make a Booking.
            </Typography>
            <Typography component="li">
              Information a Guest with an Account provides to complete their diner profile on the
              Services. This optional information is called “Guest Diner Profile Information.” Guest
              Diner Profile Information includes a photograph, birthday or anniversary details for
              the Guest and/or their partner and the Guest's hospitality preferences or dietary
              restrictions, some of which may be considered special categories of personal
              information or sensistive personal information.
            </Typography>
            <Typography component="li">
              Information a Guest provides when they make a Paid Booking. For most Paid Bookings,
              this will include billing address as well as a portion of payment information which is
              provided to us from our payment processor (such as the last four digits, the country
              of issuance and the expiration date of the payment card).
            </Typography>
            <Typography component="li">
              The emails and other communications that you send us or otherwise contribute, such as
              customer support inquiries. Please be aware that information on public parts of our
              sites is available to others.
            </Typography>
            <Typography component="li">
              Information shared with us about you by an individual who has purchased a gift card
              for you. This information is your name and email address.
            </Typography>
            <Typography component="li">
              Information shared with us about you by a person or service acting as your concierge
              to make a Booking on your behalf, or by a Merchant for purposes of making a Booking on
              your behalf.
            </Typography>
            <Typography component="li">
              Information you share with us in connection with surveys, contests or promotions.
            </Typography>
            <Typography component="li">
              Information about individuals who own or work for Merchants who use the Services.
            </Typography>
            <Typography component="li">
              Information from your interactions with the Services (including Merchant Pages). This
              includes: IP addresses, preferences, browsing history (including web pages you visited
              prior to coming to our sites) information about your browser, network or device (such
              as browser type and version, operating system, internet service provider, preference
              settings, unique device IDs and language and other regional settings), information
              about how you use the Services (such as timestamps, clicks, scrolling, browsing times,
              searches, transactions, referral pages, load times, and problems you may encounter,
              such as loading errors).
            </Typography>
            <Typography component="li">
              Information we get from our partners to support our marketing initiatives, improve our
              Services and better monitor, manage and measure our ad campaigns, such as details
              about when Guests or Merchants interact with our Services via a marketing partner or
              when an advertising partner shows a Guest one of our ads on or via its advertising
              platform.
            </Typography>
            <Typography component="li">
              Other information you submit to us directly or through Third Party Services if you use
              a Third Party Service to make a Booking and/or create an Account.
            </Typography>
          </Typography>

          <P>
            We also collect information through cookies and similar technologies when you use our
            online service or access online content. A "cookie" is a small amount of data that a
            website transfer to your computer. To learn more about how we use cokies and similar
            technologies, please see our{' '}
            <Link href="https://www.exploretock.com/cookie-policy">Cookie Policy</Link>.
          </P>

          <H>4. How we collect personal information</H>
          <P>We obtain personal information from various sources. We do this in three main ways:</P>
          <Typography component="ul">
            <Typography component="li">
              You directly provide information to us (such as by registering for an Account or
              completing a Guest diner profile).
            </Typography>
            <Typography component="li">
              When you use our Services including with technologies like cookies.
            </Typography>
            <Typography component="li">
              Information received from Merchants or other third parties (such as from a Third Party
              Service when a Guest registers for an Account or makes a Booking through such Third
              Party Service, from a Merchant or concierge when a Booking is initiated by such party
              on your behalf, or from our payment processor when a Guest makes a Paid Booking or a
              Merchant purchases a subscription).
            </Typography>
          </Typography>

          <H>5. How we use your personal information</H>
          <P>We use your personal information for the following purposes:</P>
          <Typography component="ul">
            <Typography component="li">
              <b>Provision of the Services to you and our Merchants.</b> Create and manage your
              Account, make Bookings, process payments, respond to your inquiries and enable
              Merchants to contact you and remember you and your preferences to customize and
              optimize your experiences.
            </Typography>
            <Typography component="li">
              <b>Communicating with you.</b> Communicate with you, including by sending you emails
              about your or your business' transactions and Service-related announcements.
            </Typography>
            <Typography component="li">
              <b>Surveys and contests.</b> Administer surveys, contests and other promotions.
            </Typography>
            <Typography component="li">
              <b>Promotion.</b> Promote our Services and send you tailored marketing communications
              about products, services, offers, programs and promotions of Tock and our Merchants
              and partners and measure the success of those campaigns. For example, we may send
              different marketing communications to you based on what we think may interest you
              based on other information we hold about you.
            </Typography>
            <Typography component="li">
              <b>Advertising.</b> Analyze your interactions with our Services and third parties'
              online services so we can tailor our advertising to your preferences. For example, we
              may decide not to advertise our Services to you on a social media site if you already
              have an Account or we may choose to serve you a particular advertisement based on
              previous Bookings you've made.
            </Typography>
            <Typography component="li">
              <b>Customizing the Services.</b> Provide you with customized Services. For example, we
              use your location information to determine your language preferences or display
              accurate date and time information. We may also use cookies and similar technologies
              for this purpose.
            </Typography>
            <Typography component="li">
              <b>Improving our Services.</b> Analyze and learn about how the Services are accessed
              and used, evaluate and improve our Services (including by developing new products and
              services and managing our communications) and monitor and measure the effectiveness of
              our advertising. We usually do this based on anonymous, pseudonymized or aggregated
              information which does not identify you.
            </Typography>
            <Typography component="li">
              <b>Security.</b> Ensure the security and integrity of our Services.
            </Typography>
            <Typography component="li">
              <b>Third party relationships.</b> Manage our vendor and partner relationships.
            </Typography>
            <Typography component="li">
              <b>Enforcement.</b> Enforce our {termsLink('Terms of Service')} and other legal terms
              and policies.
            </Typography>
            <Typography component="li">
              <b>Protection.</b> Protect our and others' interests, rights and property (e.g., to
              protect our Guests and Merchants from fraud or theft).
            </Typography>
            <Typography component="li">
              <b>Complying with law.</b> Comply with applicable legal requirements, such as tax and
              other government regulations and industry standards, contracts and law enforcement
              requests.
            </Typography>
          </Typography>
          <P>
            We process your personal information for the above purposes when, and where relevant:
          </P>
          <Typography component="ul">
            <Typography component="li">
              <b>Consent.</b> You have consented to the use of your personal information in a
              particular way. When you consent, you can change your mind and withdraw your consent
              at any time.
            </Typography>
            <Typography component="li">
              <b>Performance of a contract.</b> We need your personal information to provide you
              with services and products requested by you, or to respond to your inquiries. In other
              words, so we can perform our contract with you or take steps at your request. For
              example, we need your email address so you can sign into your Account.
            </Typography>
            <Typography component="li">
              <b>Legal obligation.</b> We have a legal obligation to use your personal information,
              such as to comply with applicable tax and other government regulations or to comply
              with a court order or binding law enforcement request.
            </Typography>
            <Typography component="li">
              <b>Legitimate interests.</b> We have a legitimate interest in using your personal
              information, such as to ensure the security and integrity of our Services, and
              protecting ours and other's interests, rights, and property. Please note that we
              consider and balance any potential impact on you and your rights before processing
              your information for our legitmate interest.
            </Typography>
          </Typography>

          <H>6. How we share your personal information</H>
          <P>
            We share personal information with the following entities and for the following
            purposes:
          </P>
          <Typography component="ul">
            <Typography component="li">
              <b>Affiliates.</b> We share personal information with our affiliates within the
              American Express family of companies.
            </Typography>
            <Typography component="li">
              <b>Merchants.</b> We share Guest personal information with our Merchants when a Guest
              visits the Merchant Page of a Merchant or makes a Booking with a Merchant.
              <ul style={{ listStyleType: 'circle', marginBottom: 0 }}>
                <Typography component="li">
                  If a Merchant has configured their Merchant Page to provide analytics to them,
                  including as may be provided by a Third Party Service, the Merchant will receive
                  personal information of Guests who visit that Merchant Page.
                </Typography>
                <Typography component="li">
                  When a Guest makes a Booking, we share Guest Booking Information with the
                  Merchant. “Guest Booking Information” means Basic Guest Information and may also
                  include Guest Diner Profile Information.
                </Typography>
              </ul>
            </Typography>
            <Typography component="li">
              <b>Business partners.</b> We may share personal information with business partners.
              For example, we may share your personal information with a business partner when our
              Services are integrated with their Third Party Services, but only when you have been
              informed.
            </Typography>
            <Typography component="li">
              <b>Service providers. </b> We share personal information with our service providers
              that perform services on our behalf. For example, we may use third parties to help us
              provide customer support, manage our advertisements on other sites, send marketing and
              other communications on our behalf or assist with data storage.
            </Typography>
            <Typography component="li">
              <b>Process payments. </b> We transmit some of your personal information via an
              encrypted connection to our payment processor.
            </Typography>
            <Typography component="li">
              <b>Complying with the law or protecting rights and interests. </b> We disclose your
              personal information if we determine that such disclosure is reasonably necessary to
              comply with the law, protect our or others' rights, property or interests (such as
              enforcing our {termsLink()}) or prevent fraud or abuse of Tock or our Guests or
              Merchants. In particular, we may disclose your personal information in response to
              lawful requests by public authorities, such as to meet national security or law
              enforcement requirements.
            </Typography>
            <Typography component="li">
              <b>Advertising. </b> We share personal information with third parties so they and we
              can provide you with tailored advertising and measure and monitor its effectiveness.
              For example, we may share your pseudonymized email address with a third party social
              media platform on which we advertise to avoid serving Tock ads to people who already
              use Tock.
            </Typography>
            <Typography component="li">
              <b>Business transfers. </b> If we're involved in a reorganization, merger, acquisition
              or sale of some or all of our assets, your personal information may be transferred as
              part of that deal or the negotiation of contemplated deals.
            </Typography>
          </Typography>

          <H>7. Your rights and choices</H>
          <P>
            Where applicable law requires (and subject to any relevant exceptions under law), you
            may have certain rights, including the right to access, correct, or delete personal
            information.
          </P>
          <P>
            If you are a Merchant, you can access, correct, or delete Merchant Controlled PI of your
            Guests directly in your Account.
          </P>
          <Typography component={'span'}>If you are a Guest:</Typography>
          <Typography component="ul">
            <Typography component="li" style={{ marginTop: 0 }}>
              You can access, correct, or delete Tock Controlled PI either directly in your Account
              or by contacting us at {privacyEmailAnchor()} to submit your request.
            </Typography>
            <Typography component="li">
              You will need to reach out to any Merchants with whom you have made a Booking in order
              to request they delete any Merchant Controlled PI they hold about you.
            </Typography>
            <Typography component="li">
              You can also delete your account by following the instructions on{' '}
              {helpCenterLink(undefined, '#DELETE_ACCOUNT')}. Please note that we may need to verify
              your identity in connection with your requests, and such verification process may, if
              you do not have access to your Account, require you to provide us with additional
              information we maintain about you to verify your identity. Even if you have access to
              your Account, we may request additional information if we believe it's necessary to
              verify your identity. If we are unable to verify your identity or request, we may not,
              in accordance with applicable law, be able to fulfill your request.
            </Typography>
            <Typography component="li">
              In certain circumstances, and where relevant, where a decision was made based on
              automated processing of your personal information, you may request an explanation of
              our decision. In our response we will describe the type of personal information that
              was used, the source of the personal information, and the principal factors that led
              to the decision. You will also be able to correct inaccuracies in the personal
              information used in the automated processing. If you are an EU resident, you will be
              provided, in a timely manner, meaningful information about the logic involved in any
              automated decision, as well as the significance and the potential consequences of the
              decision.
            </Typography>
            <Typography component="li">
              You can also elect not to receive marketing communications by following the
              unsubscribe instructions in such communications.
            </Typography>
          </Typography>
          <P>
            Please note that, if you request teh deletion of your personal information, we may still
            retain it when necessary to comply with the law, protect our and others' rights, resolve
            disputes or enforce our legal terms or policies, to the extent permitted under
            applicable law.
          </P>
          <P>
            You also have the right to lodge a complaint with a local competent data protection
            authority, subject to applicable law, in particular in the EU country where you live,
            work or where there may have been an infringement.
          </P>

          <P>In the UK, you may lodge such complaints with the UK supervisory authority:</P>
          <P>
            Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9
            5AF United Kingdom, Phone 0303 123 1113, Live Chat.
          </P>
          <P>
            Additionally, if we rely on consent for the processing of your personal information, you
            have the right to withdraw it at any time and free of charge.
          </P>

          <H>8. How we protect your personal information</H>
          <P>
            We main administrative, technical, and physcial safeguards, that are intended to
            appropriately protect against accidental or unlawful destruction, loss, alteration,
            disclosure, access, misuse, and any other unlawful form of processing of the personal
            information in our possession.
          </P>

          <H>9. How we retain your personal information</H>
          <P>
            We retain personal information about you for as long as your Account is active or for as
            long as needed to provide you or our Merchants with the Services. We also retain
            personal information for as long as necessary to achieve the purposes described in this
            Privacy Policy, for example, to comply with our legal obligations, to protect us in the
            event of disputes and to enforce our agreements and to protect our and others'
            interests.
          </P>
          <P>
            The precise periods for which we keep your personal information vary depending on the
            nature of the information and why we need to retain it. Factors we consider in
            determining these periods include the minimum required retention period prescribed by
            law or recommended as best practice, the period during which a claim can be made with
            respect to an agreement or other matter, whether the personal information has been
            aggregated or anonymized, and other relevant criteria. For example, the period we keep
            your email address is connected to how long your Account is active.
          </P>
          <P>
            Please note that while providing the Services, we collect and maintain aggregated and
            anonymized information which we may retain indefinitely. Aggregated and anonymized
            information does not identify you individually.
          </P>

          <H>10. Data transfers</H>
          <P>
            Personal information that you submit through the Services may be transferred to
            countries other than where you live, such as, for example, to our servers in the U.S. We
            also store personal information locally on the devices you use to access the Services.
          </P>
          <P>
            Your personal information may be transferred to countries that do not have the same data
            protection laws as the country in which you initially provided the information.
          </P>
          <P>
            We rely upon a number of means to transfer personal information that is subject to: (a)
            the European General Data Protection Regulation (“GDPR”) in accordance with Chapter V of
            the GDPR; or (b) applicable UK data privacy laws. References to GDPR and its provisions
            include the GDPR as amended and/or incorporated into UK law. These include:
          </P>
          <Typography component="ul">
            <Typography component="li">
              <b>Standard data protection clauses.</b> We transfer, in accordance with Article 46 of
              the GDPR, personal information to recipients with whom we have entered into the
              European Commission approved Standard Contractual Clauses for the transfer of personal
              data of individuals in the European Economic Area (“EEA”) outside the EEA. We
              transfer, in accordance with UK law, personal information to recipients with whom we
              have entered into the UK Information Commissioner's Office approved international data
              transfer agreement and the UK addendum to such European Commission approved contract.
            </Typography>
            <Typography component="li">
              {' '}
              <b>Other means.</b> We may, in accordance with Articles 45 and 46 of the GDPR,
              transfer personal information of individuals in the EEA to recipients that are in a
              country the European Commission or a European or UK data protection supervisory
              authority has confirmed, by decision, offers adequate levels of data protection, or
              pursuant to appropriate safeguards (such as an approved certification mechanism or
              code of conduct) together with binding, enforceable commitments from the recipient to
              apply the appropriate safeguards, including as regards data subjects' rights.
            </Typography>
          </Typography>

          <P>
            You can request further information on where to find a copy of the appropriate
            safeguards in place by contacting us at {privacyEmailAnchor()}.
          </P>

          <H>11. Updates to this Privacy Policy</H>
          <P>
            We may update this Privacy Policy from time to time to reflect changes in technology,
            law, our business operations or any other reason we determine is necessary or
            appropriate. When we make changes, we'll update the “Effective Date” at the top of the
            Privacy Policy and post it on our site. If we make material changes to it or the ways we
            process personal information, we'll notify you (by, for example, prominently posting a
            notice of the changes on our sites before they take effect or directly sending you a
            notification).
          </P>
          <P>
            We encourage you to check back periodically to review this Privacy Policy for any
            changes since your last visit.
          </P>

          <H>12. How to contact us</H>
          <P>
            If you have questions, comments or complaints about this Privacy Policy or our privacy
            practices or if you would like to exercise your rights and choices, please email us at{' '}
            {privacyEmailAnchor()}, or write to us at the address below:
          </P>
          <P>
            <br />
            Tock LLC
            <br /> Attention: Legal - Privacy
            <br /> 320 N Sangamon Street, 6th Floor
            <br />
            Chicago, IL 60607 United States
          </P>
          {/*Begin french translation */}
          <Typography component="h1" variant="heading03" align="left">
            Politique de confidentialité
          </Typography>
          <Typography variant="body01Strong" align="left">
            Date de prise d'effet: {frenchEffectivesDate}
          </Typography>

          <Typography variant="body02">
            Tock LLC (« Tock », « nous », « notre » et « nos ») tient à protéger votre
            confidentialité. La présente Politique de confidentialité décrit les renseignements
            personnels que nous recueillons, les façons dont nous utilisons et communiquons ces
            renseignements, ainsi que vos choix et vos droits. Tock appartient à{' '}
            <Link href="https://www.americanexpress.com/fr-ca/">American Express</Link>.
          </Typography>

          <Typography variant="body02">
            En utilisant n'importe lequel des services de Tock, vous confirmez avoir lu et compris
            la présente Politique de confidentialité et notre{' '}
            {cookiePolicyLink('Politique concernant les témoins')}.
          </Typography>

          <H>1. Quelques termes clés</H>
          <Typography variant="body02">
            Tous les termes clés qui ne sont pas définis dans la présente Politique de
            confidentialité ont le sens qui leur est attribué dans nos{' '}
            {termsLink('Conditions d’utilisation')}.
          </Typography>

          <H>
            2. Quels renseignements sont concernés par la présente Politique de confidentialité?
          </H>

          <Typography variant="body02">
            La présente Politique de confidentialité concerne les renseignements personnels que nous
            recueillons lorsque vous utilisez nos produits ou services, notre site Web, nos
            applications pour appareils mobiles ou comme autrement décrit dans la présente Politique
            de confidentialité. Par exemple, la présente Politique de confidentialité est applicable
            aux renseignements sur le compte des clients et des marchands, ainsi que les
            renseignements sur les façons dont les clients et les marchands utilisent nos services
            et interagissent avec eux, y compris les renseignements que les clients et les marchands
            soumettent pour obtenir du soutien à la clientèle. La présente Politique de
            confidentialité ne concerne pas les renseignements personnels de nos employés ni des
            personnes qui postulent un emploi (sauf dans la mesure où nos employés et les personnes
            qui postulent un emploi sont des clients ou des marchands).
          </Typography>

          <Typography variant="body02">
            En ce qui a trait aux renseignements personnels détenus par Tock, nous jouons différents
            rôles conformément aux lois mondiales de confidentialité des données. Afin de comprendre
            vos obligations et celles de Tock, il est important de comprendre la différence entre
            des renseignements personnels contrôlés par Tock et des renseignements personnels
            contrôlés par un marchand.
          </Typography>
          <Typography component="ul">
            <Typography component="li">
              Les « renseignements personnels contrôlés par Tock » sont les renseignements
              personnels pour lesquels Tock détermine les fins et les moyens de traitement. La
              présente Politique de confidentialité et les{' '}
              {privacySpecificLink('divers avis à propos de la confidentialité')} concernent
              exclusivement les renseignements personnels contrôlés par Tock.
            </Typography>
            <Typography component="li">
              Les « renseignements personnels contrôlés par un marchand » sont les renseignements
              personnels pour lesquels un marchand détermine les fins et les moyens de traitement.
              Les renseignements personnels contrôlés par un marchand comprennent les renseignements
              concernant les réservations des clients (définis plus bas), ainsi que toute donnée ou
              note entrée par un marchand dans les services de Tock concernant un client et les
              personnes qui l'accompagnent, le personnel du marchand ou d'autres personnes. Nous
              utilisons les renseignements contrôlés par un marchand suivant les directives de nos
              marchands, et nos marchands sont tenus d'assurer que leur collecte et leur traitement
              de renseignements personnels respectent les lois applicables et la politique de
              confidentialité qui s'appliquent à eux.
            </Typography>
          </Typography>

          <Typography variant="body02">
            Pour en savoir plus sur les pratiques des marchands concernant les renseignements
            personnels contrôlés par un marchand, veuillez lire la politique de confidentialité du
            marchand en question.
          </Typography>

          <Typography component="h4" variant="heading03">
            3. Renseignements que nous recueillons
          </Typography>

          <Typography variant="body02">
            Nous recueillons les types suivants de renseignements personnels à votre sujet :
          </Typography>
          <Typography component="ul">
            <Typography component="li">
              Les renseignements que les clients fournissent pour créer un compte ou effectuer une
              réservation, comme leur adresse de courriel, leur prénom, leur nom de famille et leur
              numéro de téléphone. Ces renseignements sont appelés des « renseignements de base sur
              les clients ». Vous n'avez pas besoin d'avoir un compte pour parcourir nos services (y
              compris les pages des marchands), mais vous en avez besoin d'un pour effectuer une
              réservation.
            </Typography>
            <Typography component="li">
              Les renseignements que les clients qui possèdent un compte fournissent pour créer leur
              profil de client dans les services de Tock. Ces renseignements facultatifs sont
              appelés des « renseignements de profil de client » Les renseignements de profil de
              client comprennent une photo, la date de naissance ou la date d'anniversaire des
              clients et (ou) de leur conjoint(e), ainsi que les préférences d'accueil et les
              restrictions alimentaires des clients. Certains de ces renseignements peuvent être
              considérés comme appartenant à des catégories spéciales de renseignements personnels
              ou considérés comme des renseignements personnels sensibles.
            </Typography>
            <Typography component="li">
              Les renseignements que les clients fournissent lorsqu'ils effectuent une réservation
              payée. Pour la plupart des réservations payées, ces renseignements comprennent une
              adresse de facturation ainsi qu'une partie des renseignements de paiement qui nous
              sont fournis par notre société de traitement de paiements (comme les quatre derniers
              chiffres, le pays d'émission et la date d'expiration de la carte de paiement).
            </Typography>
            <Typography component="li">
              Les courriels et autres communications que vous nous envoyez, comme les questions
              posées au service de soutien à la clientèle. Veuillez prendre note que les
              renseignements figurant sur les parties publiques de nos sites sont accessibles par
              d'autres personnes.
            </Typography>
            <Typography component="li">
              Les renseignements à votre sujet qui nous sont fournis par une autre personne qui a
              acheté une carte-cadeau pour vous. Ces renseignements sont votre nom et votre adresse
              de courriel.
            </Typography>
            <Typography component="li">
              Les renseignements à votre sujet qui nous sont fournis par une autre personne ou par
              un service de conciergerie qui effectue une réservation pour vous.
            </Typography>
            <Typography component="li">
              Les renseignements que vous nous fournissez dans le cadre de sondages, concours et
              promotions.
            </Typography>
            <Typography component="li">
              Les renseignements au sujet des personnes qui sont les propriétaires d'entreprise de
              marchands ou qui travaillent pour des marchands qui utilisent les services de Tock.
            </Typography>
            <Typography component="li">
              Les renseignements tirés de vos interactions avec les services de Tock, y compris les
              pages des marchands. Ceci comprend : des adresses IP, des préférences, des historiques
              de navigation (y compris les pages visitées avant d'accéder à nos sites), des
              renseignements sur votre navigateur, réseau et appareil (comme le type et la version
              de navigateur, le système d'exploitation, le fournisseur d'accès Internet, les
              paramètres de préférences, l'identifiant unique d'appareil, la langue et d'autres
              paramètres régionaux), des renseignements sur les façons dont vous utilisez les
              services de Tock (comme les heures enregistrées, les clics, le défilement, les moments
              de navigation, les recherches, les transactions, les pages de renvoi, le temps de
              chargement et les problèmes que vous pouvez éprouver, comme des erreurs de
              chargement).
            </Typography>
            <Typography component="li">
              Les renseignements que nous obtenons de nos partenaires pour soutenir nos initiatives
              de marketing, améliorer nos services et mieux surveiller, gérer et mesurer nos
              campagnes publicitaires, comme le moment où les clients et les marchands interagissent
              avec nos services par l'entremise d'un partenaire de marketing, ou le moment auquel un
              partenaire publicitaire montre à un client une de nos publicités sur sa plateforme de
              publicité ou par l'entremise de cette dernière.
            </Typography>
            <Typography component="li">
              D'autres renseignements que vous nous soumettez directement ou par l'entremise de
              tiers si vous utilisez un service de tiers pour effectuer une réservation ou créer un
              compte.
            </Typography>
          </Typography>

          <Typography variant="body02">
            Nous recueillons aussi des renseignements à l'aide de témoins et de technologies
            similaires quand vous utilisez nos services en ligne ou accédez à du contenu en ligne.
            Un « témoin » est une petite quantité de données qu'un site Web transmet à votre
            ordinateur. Pour en savoir plus sur les façons dont nous utilisons des témoins et des
            technologies similaires, veuillez lire notre{' '}
            {cookiePolicyLink('politique sur les témoins')}.
          </Typography>

          <Typography component="h4" variant="heading03">
            4. Comment nous recueillons des renseignements personnels
          </Typography>

          <Typography variant="body02">
            Nous obtenons des renseignements personnels de diverses sources. Nous avons trois façons
            principales d'en obtenir :
          </Typography>
          <Typography component="ul">
            <Typography component="li">
              Vous nous fournissez directement des renseignements (comme lorsque vous créez un
              compte ou un profil de client).
            </Typography>
            <Typography component="li">
              Lorsque vous utilisez nos services, y compris aux moyens de technologies comme les
              témoins.
            </Typography>
            <Typography component="li">
              Les renseignements que nous recevons de la part de marchands ou d'autres tiers (comme
              lorsqu'un client crée un compte ou effectue une réservation par l'entremise d'un
              service de tiers, d'un marchand ou d'un service de conciergerie) ou de la part de
              notre société de traitement lorsqu'un client effectue une réservation payée ou
              lorsqu'un marchand achète un abonnement).
            </Typography>
          </Typography>

          <Typography component="h4" variant="heading03">
            5. Comment nous utilisons vos renseignements personnels
          </Typography>

          <Typography variant="body02">
            Nous utilisons vos renseignements personnels aux fins suivantes :
          </Typography>
          <Typography component="ul">
            <Typography component="li">
              <b>Vous fournir des services et en fournir à nos marchands.</b> Créer et gérer votre
              compte, effectuer des réservations, traiter des paiements, répondre à vos requêtes,
              permettre aux marchands de communiquer avec vous, et nous souvenir de vous et de vos
              préférences afin de personnaliser et d'optimiser vos expériences.
            </Typography>
            <Typography component="li">
              <b>Communiquer avec vous.</b> Communiquer avec vous, y compris vous envoyer des
              courriels au sujet de vos transactions et de celles votre entreprise, et vous
              transmettre des annonces se rattachant à nos services.
            </Typography>
            <Typography component="li">
              <b>Sondages et concours.</b> Réaliser des sondages, des concours et d'autres
              promotions.
            </Typography>
            <Typography component="li">
              <b>Promotion.</b> Faire la promotion de nos services et vous envoyer des
              communications de marketing personnalisées au sujet de produits, de services,
              d'offres, de programmes et de promotions de Tock, de nos marchands et de nos
              partenaires.
            </Typography>
            <Typography component="li">
              <b>Publicité.</b> Analyser vos interactions avec nos services et les services en ligne
              de tiers pour que nous puissions adapter nos publicités à vos préférences. Par
              exemple, nous pourrions décider de ne pas vous présenter des publicités pour nos
              services sur un réseau social si vous avez déjà un compte, ou nous pourrions vous
              présenter une publicité en particulier en fonction de vos réservations précédentes.
            </Typography>
            <Typography component="li">
              <b>Améliorer nos services.</b> Analyser nos services et apprendre comment ils sont
              accédés et utilisés, évaluer et améliorer nos services (y compris par la création de
              nouveaux produits et services ainsi que la gestion de nos communications), et
              surveiller et mesurer l'efficacité de nos publicités. Nous le faisons habituellement
              au moyen de renseignements anonymisés, pseudonymisés ou agrégés qui ne permettent pas
              de vous identifier.
            </Typography>
            <Typography component="li">
              Relations avec des tiers. Gérer nos relations avec des fournisseurs et des
              partenaires.
            </Typography>
            <Typography component="li">
              <b>Mise en application.</b> Mettre en application nos{' '}
              {termsLink(`conditions d'utilisation`)}, d'autres conditions légales et nos
              politiques.
            </Typography>
            <Typography component="li">
              <b>Protection.</b> Protéger nos intérêts, droits et biens, et ceux d'autres personnes
              et entités (comme protéger nos clients et marchands contre la fraude ou le vol).
            </Typography>
            <Typography component="li">
              <b>Respecter les lois.</b> Respecter les exigences légales applicables, comme celles
              qui concernent la fiscalité et d'autres réglementations gouvernementales et normes
              industrielles, remplir nos obligations contractuelles et répondre aux demandes
              d'organismes d'application de la loi.
            </Typography>
          </Typography>

          <Typography variant="body02">
            Nous traitons vos renseignements personnels aux fins susmentionnées quand et là où il
            est pertinent de le faire :
          </Typography>
          <Typography component="ul">
            <Typography component="li">
              <b>Consentement.</b> Vous avez consenti à l'utilisation de vos renseignements
              personnels d'une façon particulière. Si vous avez donné votre consentement, vous
              pouvez changer d'idée et le retirer à tout moment.
            </Typography>
            <Typography component="li">
              <b>Remplir les obligations d'un contrat.</b> Nous avons besoin de vos renseignements
              personnels pour vous fournir les produits et services que vous demandez, ainsi que
              pour répondre à vos requêtes. Autrement dit, afin de remplir le contrat que nous avons
              conclu avec vous ou prendre des mesures que vous demandez. Par exemple, nous avons
              besoin de votre adresse de courriel afin de vous permettre d'accéder à votre compte.
            </Typography>
            <Typography component="li">
              <b>Obligation en vertu de la loi.</b> Nous sommes obligés par la loi d'utiliser vos
              renseignements personnels à certaines fins, comme respecter les lois fiscales et
              d'autres réglementations gouvernementales, ainsi que pour répondre à une ordonnance
              d'un tribunal ou à une demande contraignante d'un organisme d'application de la loi.
            </Typography>
            <Typography component="li">
              <b>Intérêts légitimes.</b> Il est dans notre intérêt légitime d'utiliser vos
              renseignements personnels à certaines fins, comme veiller à la sécurité et à
              l'intégrité de nos services, et protéger nos droits, nos biens et nos intérêts, de
              même que ceux des autres. Veuillez noter que nous évaluons et cherchons à minimiser
              toute incidence potentielle sur vous et vos droits avant de traiter vos renseignements
              personnels pour nos intérêts légitimes.
            </Typography>
          </Typography>

          <Typography component="h4" variant="heading03">
            6. Comment nous communiquons vos renseignements personnels
          </Typography>

          <Typography variant="body02">
            Nous communiquons vos renseignements personnels aux entités suivantes, aux fins
            suivantes :
          </Typography>
          <Typography component="ul">
            <Typography component="li">
              <b>Entreprises affiliées.</b> Nous communiquons des renseignements personnels à nos
              entreprises affiliées qui font partie de la famille d'entreprises American Express.
            </Typography>
            <Typography component="li">
              <b>Marchands.</b> Nous communiquons des renseignements personnels de nos clients à nos
              marchands lorsque nos clients visitent une page de marchand ou effectuent une
              réservation auprès d'un marchand.
            </Typography>
            <Typography component="ul">
              <Typography component="li">
                Si un marchand a configuré sa page de marchand de façon à ce qu'elle lui fournisse
                des analyses, y compris lorsque ces analyses sont fournies par un tiers, ce marchand
                recevra des renseignements personnels des clients qui visitent sa page de marchand.
              </Typography>
              <Typography component="li">
                Lorsqu'un client effectue une réservation, nous communiquons des renseignements sur
                la réservation du client au marchand. « Renseignements de réservation de client »
                signifie des renseignements de base sur les clients, qui peuvent comprendre des
                renseignements de profil de client.
              </Typography>
            </Typography>

            <Typography component="li">
              <b>Partenaires d'affaires.</b> Nous pouvons aussi communiquer des renseignements
              personnels à nos partenaires d'affaires. Par exemple, nous pourrions communiquer de
              vos renseignements personnels à un partenaire d'affaires lorsque nos services sont
              intégrés à ses services, mais seulement lorsque vous en avez été avisé(e).
            </Typography>
            <Typography component="li">
              <b>Fournisseurs de services.</b> Nous communiquons des renseignements personnels avec
              nos fournisseurs de services qui fournissent des services pour nous. Par exemple, nous
              pourrions faire appel à des tiers pour nous aider à fournir du soutien à la clientèle,
              à gérer nos publicités sur d'autres sites, envoyer des communications de marketing et
              d'autres types pour nous, ou encore pour nous procurer des services de stockage de
              données.
            </Typography>
            <Typography component="li">
              <b>Traiter des paiements.</b> Nous transmettons par connexion chiffrée certains de vos
              renseignements personnels à notre société de traitement de paiements.
            </Typography>
            <Typography component="li">
              <b>Respecter la loi ou protéger nos droits et intérêts.</b> Nous divulguons vos
              renseignements personnels lorsque nous déterminons qu'une telle divulgation est
              raisonnablement nécessaire pour respecter la loi, protéger nos droits, nos biens et
              nos intérêts de même que ceux des autres (comme faire respecter nos{' '}
              {termsLink(`conditions
              d'utilisation`)}
              ) ou pour protéger Tock, nos clients ou nos marchands contre la fraude ou l'abus.
              Notamment, nous pouvons divulguer vos renseignements personnels en réponse à une
              demande légale de la part d'une autorité publique à des fins de sécurité nationale ou
              d'application de la loi, entre autres.
            </Typography>
            <Typography component="li">
              <b>Publicité.</b> Nous communiquons des renseignements personnels à des tiers pour que
              ces tiers et nous puissions vous présenter des publicités personnalisées, et mesurer
              et surveiller leur efficacité. Par exemple, nous pourrions communiquer votre adresse
              de courriel pseudonymisée à un réseau social tiers sur lequel nous présentons des
              publicités de façon à éviter de présenter des publicités de Tock aux personnes qui
              utilisent déjà Tock.
            </Typography>
            <Typography component="li">
              <b>Transferts d'entreprise.</b> Si nous sommes visés par une réorganisation, une
              fusion, une acquisition ou la vente de l'ensemble ou d'une partie de nos actifs, vos
              renseignements personnels pourraient être transférés dans le cadre de la transaction
              ou de la négociation d'une transaction envisagée.
            </Typography>
          </Typography>

          <Typography component="h4" variant="heading03">
            7. Vos droits et choix
          </Typography>

          <Typography variant="body02">
            Si les lois applicables l'exigent (et sous réserve de toute exception prévue par
            lesdites lois), vous pourriez bénéficier de certains droits, y compris les droits
            d'accéder, de corriger et de supprimer vos renseignements personnels.
          </Typography>

          <Typography variant="body02">
            Si vous êtes marchand, vous pouvez accéder aux renseignements personnels contrôlés par
            un marchand, les corriger et les supprimer directement dans votre compte.
          </Typography>

          <Typography variant="body02">Si vous êtes client ou cliente :</Typography>
          <Typography component="ul">
            <Typography component="li">
              Vous pouvez accéder aux renseignements personnels contrôlés par Tock, les corriger et
              les supprimer directement dans votre compte, ou en nous envoyant votre demande à{' '}
              {privacyEmailAnchor()}.
            </Typography>
            <Typography component="li">
              Vous devrez communiquer avec tout marchand auprès duquel vous avez effectué une
              réservation afin de demander qu'il supprime vos renseignements personnels contrôlés
              par un marchand.
            </Typography>
            <Typography component="li">
              Vous pouvez aussi supprimer votre compte en suivant les directives présentées sur{' '}
              {helpCenterLink(`cette page du Centre d'aide pour clients`)}. Prenez note que nous
              pourrions devoir vérifier votre identité pour remplir vos demandes, et que cette
              vérification d'identité pourrait, si vous n'avez pas accès à votre compte, exiger que
              vous fournissiez d'autres renseignements que nous détenons à votre sujet. Même si vous
              avez accès à votre compte, nous pourrions vous demander d'autres renseignements si
              nous estimons que c'est nécessaire pour vérifier votre identité. Si nous ne pouvons
              pas vérifier votre identité ou votre demande, nous pourrions être dans
              l'impossibilité, conformément aux lois applicables, de remplir votre demande.
            </Typography>
            <Typography component="li">
              Dans certaines situations, et lorsqu'il est pertinent de le faire, si une décision a
              été prise en fonction d'un traitement automatisé de vos renseignements personnels,
              vous pouvez demander une explication de notre décision. Dans notre réponse, nous
              décrirons le type de renseignements personnels utilisés, la source des renseignements
              personnels et les principaux facteurs qui ont mené à la décision. Vous pourrez aussi
              corriger les inexactitudes dans les renseignements personnels utilisés pour le
              traitement automatisé. Si vous résidez dans l'Union européenne, des renseignements
              significatifs vous seront promptement fournis concernant la logique suivie pour toute
              décision automatisée, et concernant le sens et les conséquences potentielles de la
              décision.
            </Typography>
            <Typography component="li">
              Vous pouvez aussi choisir que nous ne vous envoyions pas de communications de
              marketing en suivant les directives de désabonnement à de telles communications.
            </Typography>
          </Typography>

          <Typography variant="body02">
            Veuillez noter que si vous demandez la suppression de vos renseignements personnels,
            nous pouvons tout de même les conserver dans la mesure permise par les lois applicables
            lorsqu'il est nécessaire de le faire pour respecter les lois applicables, pour protéger
            nos droits et ceux des autres, pour régler des différends ou pour faire respecter nos
            conditions juridiques ou nos politiques.
          </Typography>

          <Typography variant="body02">
            Vous avez aussi le droit de soumettre une plainte à une autorité locale compétente de
            protection des données, conformément aux lois applicables, notamment dans le pays de
            l'Union européenne où vous résidez et travaillez, ou dans lequel une infraction a
            possiblement eu lieu.
          </Typography>

          <Typography variant="body02">
            Au Royaume-Uni, vous pouvez soumettre une plainte de la sorte à l'autorité de
            supervision pertinente du Royaume-Uni :
          </Typography>

          <Typography variant="body02">
            Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9
            5AF United Kingdom. Numéro de téléphone : 0303 123 1113, clavardage en temps réel.
          </Typography>

          <Typography variant="body02">
            De plus, lorsque nous dépendons de votre consentement pour le traitement de vos
            renseignements personnels, vous avez le droit de retirer votre consentement à tout
            moment et sans frais.
          </Typography>

          <Typography component="h4" variant="heading03">
            8. Comment nous protégeons vos renseignements personnels
          </Typography>

          <Typography variant="body02">
            Nous avons en place des protections administratives, techniques et physiques visant la
            protection adéquate des renseignements personnels que nous détenons contre leur
            destruction, leur perte, leur modification, leur divulgation, leur accès et leur abus de
            façon accidentelle ou illégale, et contre toute autre forme illégale de traitement.
          </Typography>

          <Typography component="h4" variant="heading03">
            9. Comment nous conservons vos renseignements personnels
          </Typography>

          <Typography variant="body02">
            Nous conservons vos renseignements personnels tant et aussi longtemps que votre compte
            est actif, ou tant et aussi longtemps qu'ils sont nécessaires pour vous fournir nos
            services ou les fournir à nos marchands. Nous conservons également des renseignements
            tant et aussi longtemps qu'ils sont nécessaires aux fins décrites dans la présente
            Politique de confidentialité, par exemple, pour remplir nos obligations légales, pour
            nous protéger en cas de différend, pour faire respecter nos ententes, et pour protéger
            nos intérêts et ceux des autres.
          </Typography>

          <Typography variant="body02">
            La durée exacte pendant laquelle nous conservons vos renseignements personnels varie en
            fonction de la nature des renseignements et des raisons pour lesquelles nous devons les
            conserver. Les facteurs dont nous tenons compte pour déterminer cette durée comprend la
            durée minimale de conservation prévue par la loi ou recommandée comme meilleure
            pratique, la période au cours de laquelle une réclamation peut être présentée concernant
            une entente ou autre question, l'agrégation ou l'anonymisation des renseignements
            personnels, ainsi que d'autres critères pertinents. Par exemple, la durée de
            conservation de votre adresse de courriel est reliée à combien de temps votre compte est
            actif.
          </Typography>

          <Typography variant="body02">
            Veuillez noter qu'en fournissant nos services, nous recueillons et entretenons des
            renseignements agrégés et anonymisés que nous pourrions conserver pour une période
            indéterminée. Les renseignements agrégés et anonymisés ne permettent pas de vous
            identifier individuellement.
          </Typography>

          <Typography component="h4" variant="heading03">
            10. Transferts de données
          </Typography>

          <Typography variant="body02">
            Les renseignements personnels que vous soumettez par le biais de nos services pourraient
            être transférés vers des pays autres que votre pays de résidence, comme aux États-Unis
            où se trouvent nos serveurs. Nous stockons aussi des renseignements personnels
            localement sur les appareils que vous utilisez pour accéder à nos services.
          </Typography>

          <Typography variant="body02">
            Vos renseignements personnels pourraient être transférés vers des pays qui n'ont pas les
            mêmes lois de protection des données que le pays dans lequel vous avez originalement
            fourni ces renseignements.
          </Typography>

          <Typography variant="body02">
            Nous utilisons plusieurs moyens pour transférer des renseignements personnels, et ces
            moyens sont soumis à la réglementation suivante : a) le Règlement général sur la
            protection des données de l'UE, conformément à son chapitre V; ou b) les lois
            applicables de confidentialité des données du Royaume-Uni. Toute référence au Règlement
            général sur la protection des données et à ses dispositions comprend toute nouvelle
            version du Règlement et (ou) son intégration dans les lois du Royaume-Uni. Y compris :
          </Typography>
          <Typography component="ul">
            <Typography component="li">
              <b>Dispositions de base de protection de données.</b> Nous transférons, conformément à
              l'article 46 du Règlement général sur la protection des données, des renseignements
              personnels à des destinataires avec qui nous avons conclu un contrat comportant les
              dispositions habituelles de la Commission européenne concernant le transfert de
              données personnelles de personnes dans l'Espace économique européen hors de l'Espace
              économique européen. Nous transférons, conformément aux lois du Royaume-Uni, des
              renseignements personnels à des destinataires avec qui nous avons conclu l'entente de
              transfert international de données approuvée par l'Information Commissioner's Office
              du Royaume-Uni, et l'addenda du Royaume-Uni pour ledit contrat approuvé par la
              Commission européenne.
            </Typography>
            <Typography component="li">
              <b>Autres moyens.</b> Nous pouvons, conformément aux articles 45 et 46 du Règlement
              général sur la protection des données, transférer des renseignements personnels à des
              personnes de l'Espace économique européen à des destinataires qui sont dans un pays
              qui a été jugé, par décision de la Commission européenne ou d'une autre autorité
              européenne ou du Royaume-Uni de supervision de la protection des données, comme
              offrant un degré suffisant de protection de données, ou qui utilise des mécanismes de
              protection adéquats (comme un mécanisme de certification ou un code de conduite
              approuvé) combinés avec des engagements contraignants et exécutoires de la part du
              destinataire d'utiliser des mécanismes de protection adéquats, y compris concernant
              les droits des sujets des données.
            </Typography>
          </Typography>

          <Typography variant="body02">
            Vous pouvez nous demander où trouver une copie des mécanismes de protection adéquats en
            place par courriel à {privacyEmailAnchor()}.
          </Typography>

          <Typography component="h4" variant="heading03">
            11. Modifications de la présente Politique de confidentialité
          </Typography>

          <Typography variant="body02">
            Nous pouvons modifier la présente Politique de confidentialité de temps à autre pour
            tenir compte de changements touchant les technologies, les lois et nos activités
            d’affaires, ou pour toute autre raison que nous jugeons appropriée. Lorsque nous
            apportons des changements, nous modifions la « date de prise d’effet » au début de la
            Politique de confidentialité, et nous publions la nouvelle version de la Politique de
            confidentialité sur notre site. Si nous apportons des changements importants, nous vous
            en aviserons en publiant un avis de modification sur notre site avant la prise d’effet,
            ou en vous envoyant directement un avis de modification.
          </Typography>

          <Typography variant="body02">
            Nous vous encourageons à régulièrement revisiter la présente Politique de
            confidentialité pour toute modification depuis votre dernière visite.
          </Typography>

          <Typography component="h4" variant="heading03">
            12. Comment communiquer avec nous
          </Typography>

          <Typography variant="body02">
            Pour toute question, remarque ou plainte à propos de la présente Politique de
            confidentialité ou de nos pratiques de confidentialité, ou pour exercer vos droits et
            vos choix, veuillez nous envoyer un courriel à {privacyEmailAnchor()}, ou nous envoyer
            une lettre à l’adresse ci-dessous :
          </Typography>

          <Typography variant="body02">
            Tock LLC
            <br /> Attention: Legal - Privacy
            <br /> 320 N Sangamon Street, 6th Floor
            <br />
            Chicago, IL 60607 United States
          </Typography>
        </Container>
      </div>
    </>
  );
};

export default PrivacyRoute;
